import { showSnackbar, snackbarTypes } from 'src/redux/snackbar'
import { fetchDispatch } from 'data/helpers'

// Action Types
export const SEND_MESSAGE_LOADING = 'aiJobs/SEND_MESSAGE_LOADING'
export const SEND_MESSAGE_SUCCESS = 'aiJobs/SEND_MESSAGE_SUCCESS'
export const SEND_MESSAGE_FAILURE = 'aiJobs/SEND_MESSAGE_FAILURE'

export const UPDATE_AI_JOB_FAVORITE = 'aiJobs/UPDATE_AI_JOB_FAVORITE'

export const ADD_CHAT_MESSAGE = 'aiJobs/ADD_CHAT_MESSAGE'
export const CLEAR_CHAT_HISTORY = 'aiJobs/CLEAR_CHAT_HISTORY'
export const SET_AI_TYPING = 'aiJobs/SET_AI_TYPING'
export const RESET_CONVERSATION = 'aiJobs/RESET_CONVERSATION'
export const SET_MESSAGE_FEEDBACK = 'aiJobs/SET_MESSAGE_FEEDBACK'
export const SET_JOB_FEEDBACK = 'aiJobs/SET_JOB_FEEDBACK'
export const SET_MOBILE_DRAWER_STATE = 'aiJobs/SET_MOBILE_DRAWER_STATE'
export const SET_AI_MODE = 'aiJobs/SET_AI_MODE'

// Action Creators
export const sendMessageLoading = () => ({ type: SEND_MESSAGE_LOADING })
export const sendMessageSuccess = payload => ({ type: SEND_MESSAGE_SUCCESS, payload })
export const sendMessageFailure = payload => ({ type: SEND_MESSAGE_FAILURE, payload })

export const updateAiJobFavorite = payload => ({ type: UPDATE_AI_JOB_FAVORITE, payload })

export const addChatMessage = payload => ({ type: ADD_CHAT_MESSAGE, payload })
export const clearChatHistory = () => ({ type: CLEAR_CHAT_HISTORY })
export const setAiTyping = payload => ({ type: SET_AI_TYPING, payload })
export const resetConversation = () => ({ type: RESET_CONVERSATION })
export const setMessageFeedback = payload => ({ type: SET_MESSAGE_FEEDBACK, payload })
export const setJobFeedback = payload => ({ type: SET_JOB_FEEDBACK, payload })
export const setMobileDrawerState = payload => ({ type: SET_MOBILE_DRAWER_STATE, payload })
export const setAIMode = payload => ({ type: SET_AI_MODE, payload })

// Thunk Actions
export const sendChatMessage = ({ message, conversationId }) => {
  return async dispatch => {
    dispatch(sendMessageLoading())
    dispatch(setAiTyping(true))

    try {
      const response = await fetchDispatch({
        path: '/api/job_chatbot/message',
        method: 'POST',
        values: {
          content: message,
          conversationId,
        },
      })

      dispatch(sendMessageSuccess(response))

      // Add AI response to chat history
      if (response.content) {
        dispatch(addChatMessage({
          type: 'ai',
          message: response.content,
          metadata: {
            chatbotMessageId: response.chatbotMessageId,
            jobsCount: response.rankedJobs?.length || 0,
          }
        }))
      }

      dispatch(setAiTyping(false))
    } catch (error) {
      dispatch(
        showSnackbar(
          'Sorry! We encountered an error. Please try again.',
          snackbarTypes.ERROR,
        ),
      )
      dispatch(sendMessageFailure(error.response))
      dispatch(setAiTyping(false))
    }
  }
}

const FEEDBACK_REASON_VALUES = {
  didnt_understand: 1,
  wrong_specialty: 2,
  wrong_location: 3,
  salary_too_low: 4,
  irrelevant_jobs: 5,
  missing_criteria: 6,
  inaccurate: 7,
  not_helpful: 8,
  relevant_jobs: 9,
  right_specialty: 10,
  good_location: 11,
  fair_salary: 12,
  understood_needs: 13,
  helpful_response: 14,
  other: 15,
}

const VOTE_VALUES = { up: 1, down: 0 }

export const submitMessageFeedback = ({ chatbotMessageId, conversationId, vote, reasons, comment }) => {
  fetchDispatch({
    path: '/api/job_chatbot/feedback',
    values: {
      conversationId,
      chatbotMessageId,
      vote: VOTE_VALUES[vote],
      messageReasons: reasons.map(r => FEEDBACK_REASON_VALUES[r]).filter(Boolean),
      comment,
    },
    method: 'POST',
  }).catch(() => {})
}

const JOB_FEEDBACK_REASON_VALUES = {
  inaccurate_match: 1,
  wrong_specialty: 2,
  wrong_location: 3,
  salary_mismatch: 4,
  missing_requirements: 5,
  justification_unclear: 6,
  accurate_match: 7,
  good_specialty_fit: 8,
  location_right: 9,
  salary_meets_expectations: 10,
  criteria_match: 11,
  other: 12,
}

export const submitJobFeedback = ({ jobId, conversationId, chatbotMessageId, vote, reasons, comment }) => {
  fetchDispatch({
    path: '/api/job_chatbot/job_feedback',
    values: {
      conversationId,
      chatbotMessageId,
      jobId,
      vote: VOTE_VALUES[vote],
      jobReasons: reasons.map(r => JOB_FEEDBACK_REASON_VALUES[r]).filter(Boolean),
      comment,
    },
    method: 'POST',
  }).catch(() => {})
}

// Initial State
const initialState = {
  loading: false,
  conversationId: null,
  rankedJobs: [],
  jobListingArguments: null,
  chatHistory: [],
  isAiTyping: false,
  mobileDrawerState: 'full', // 'full' | 'minimized' | 'expanded' | 'hidden'
  isAIMode: false,
}

// Reducer
export const aiJobSearchReducer = (state = initialState, action) => {
  switch (action.type) {
    case SEND_MESSAGE_LOADING:
      return { ...state, loading: true }

    case SEND_MESSAGE_SUCCESS:
      return {
        ...state,
        loading: false,
        conversationId: action.payload.conversationId || state.conversationId,
        // null means "no search activity" — keep existing jobs displayed.
        // [] or [...] means a search was performed — replace with new results.
        rankedJobs: action.payload.rankedJobs != null
          ? action.payload.rankedJobs.map(rj => ({ ...rj, chatbotMessageId: action.payload.chatbotMessageId }))
          : state.rankedJobs,
        jobListingArguments: action.payload.jobListingArguments ?? state.jobListingArguments,
      }

    case SEND_MESSAGE_FAILURE:
      return { ...state, loading: false }

    case UPDATE_AI_JOB_FAVORITE:
      return {
        ...state,
        rankedJobs: state.rankedJobs.map(rankedJob =>
          rankedJob.job?.id === action.payload.jobId
            ? {
                ...rankedJob,
                job: {
                  ...rankedJob.job,
                  candidateJob: {
                    ...rankedJob.job.candidateJob,
                    favorite: action.payload.favorite,
                  },
                },
              }
            : rankedJob
        ),
      }

    case SET_MESSAGE_FEEDBACK:
      return {
        ...state,
        chatHistory: state.chatHistory.map(msg =>
          msg.id === action.payload.messageId
            ? { ...msg, feedback: { ...action.payload } }
            : msg
        ),
      }

    case SET_JOB_FEEDBACK:
      return {
        ...state,
        rankedJobs: state.rankedJobs.map(rankedJob =>
          rankedJob.job?.id === action.payload.jobId
            ? { ...rankedJob, feedback: { ...action.payload } }
            : rankedJob
        ),
      }

    case ADD_CHAT_MESSAGE:
      return {
        ...state,
        chatHistory: [...state.chatHistory, {
          id: Date.now() + Math.random(),
          type: action.payload.type,
          message: action.payload.message,
          timestamp: new Date().toISOString(),
          ...action.payload.metadata
        }]
      }

    case CLEAR_CHAT_HISTORY:
      return {
        ...state,
        chatHistory: []
      }

    case SET_AI_TYPING:
      return {
        ...state,
        isAiTyping: action.payload
      }

    case RESET_CONVERSATION:
      return {
        ...initialState,
        mobileDrawerState: state.mobileDrawerState,
      }

    case SET_MOBILE_DRAWER_STATE:
      return {
        ...state,
        mobileDrawerState: action.payload
      }

    case SET_AI_MODE:
      return {
        ...state,
        isAIMode: action.payload
      }

    default:
      return state
  }
}
